/* Owner reviews rail: the motion, a finger drag, and a pause button.

   The rail used to be a pure CSS marquee (fx-reviews.css keeps it as the
   fallback for when this file does not run). A keyframe animation cannot be
   dragged or nudged, so once this file runs it adds .is-js to the section,
   the keyframes stop, and the rail is moved here instead:

   - AUTO SCROLL at the same speed on every screen, in pixels a second: the
     desktop pace, one full set of cards in the section's "Seconds for one
     full loop" setting (about 49px a second at 110). A phone used to run
     faster than that: the old rail's width came out wrong there and it ran
     at about 65px a second on an iPhone, then into a long empty stretch.
   - DRAG with a finger or a mouse, left and right. Vertical swipes still
     scroll the page (touch-action: pan-y on the track). Letting go carries a
     little momentum, then the auto scroll picks up again. A drag never opens
     the card it started on.
   - PAUSE AND PLAY with the button under the rail. Paused stays paused until
     it is pressed again; dragging while paused moves the rail and leaves it
     paused. With reduced motion asked for, it starts paused.
   - A mouse over the rail, or keyboard focus in it, holds it still, as the
     CSS version did. Keyboard focus on a card brings that card into view.

   The rail holds the cards twice, so the offset wraps at the distance between
   the first card and its copy, measured rather than assumed, which keeps the
   loop seamless. Off screen or in a background tab, nothing runs. */
(function () {
  'use strict';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  /* One card and its gap at desktop size, from fx-reviews.css (340px card,
     20px gap). The pace is set from this on every screen, so a phone, whose
     cards are narrower, moves at the desktop speed rather than faster. */
  var DESKTOP_PITCH = 360;
  var DRAG_START = 6;
  var FRICTION = 0.94;

  function init(section) {
    if (section.getAttribute('data-fxrv-ready')) return;
    var track = section.querySelector('.fxrv__track');
    var rail = section.querySelector('.fxrv__rail');
    var btn = section.querySelector('[data-fxrv-toggle]');
    if (!track || !rail) return;
    var cards = rail.querySelectorAll('.fxrv__card');
    if (cards.length < 2) return;
    section.setAttribute('data-fxrv-ready', '1');
    section.classList.add('is-js');

    var offset = 0;
    var period = 0;
    var paused = !!(REDUCED && REDUCED.matches);
    var hover = false;
    var focused = false;
    var onScreen = true;
    var velocity = 0;
    var last = 0;
    var raf = 0;

    var drag = null;
    var swallowNext = false;

    /* The copy of the first card, if something has hidden the second pass,
       has no box; the loop then wraps after the last real card instead. */
    function measure() {
      var n = cards.length / 2;
      var first = cards[0];
      var copy = cards[n];
      period = copy && copy.getClientRects().length ? copy.offsetLeft - first.offsetLeft : 0;
      if (period <= 0) {
        var lastReal = cards[n - 1];
        var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        period = lastReal.offsetLeft + lastReal.offsetWidth + gap - first.offsetLeft;
      }
    }

    function wrap(x) {
      if (!period) return x;
      x = x % period;
      return x < 0 ? x + period : x;
    }

    function paint() {
      rail.style.transform = 'translate3d(' + (-offset).toFixed(2) + 'px, 0, 0)';
    }

    /* Pixels a second. */
    function pace() {
      var s = parseFloat(getComputedStyle(section).getPropertyValue('--fxrv-speed'));
      if (!(s > 0)) s = 110;
      return (cards.length / 2) * DESKTOP_PITCH / s;
    }

    function running() {
      return onScreen && !document.hidden;
    }

    function frame(now) {
      raf = 0;
      /* Capped so a stalled frame never jumps the rail far; a hidden tab
         resets the clock instead (visibilitychange below). */
      var dt = last ? Math.min(now - last, 250) : 16;
      last = now;
      if (!drag) {
        if (Math.abs(velocity) > 0.02) {
          offset = wrap(offset + velocity * dt);
          velocity *= Math.pow(FRICTION, dt / 16);
        } else {
          velocity = 0;
          if (!paused && !hover && !focused && period) {
            offset = wrap(offset + pace() * (dt / 1000));
          }
        }
        paint();
      }
      schedule();
    }

    function schedule() {
      if (raf || !running()) { if (!running()) last = 0; return; }
      raf = requestAnimationFrame(frame);
    }

    /* ---- the button ---- */
    function syncButton() {
      if (!btn) return;
      btn.hidden = false;
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      btn.setAttribute('aria-label', paused ? 'Play reviews' : 'Pause reviews');
      btn.classList.toggle('is-paused', paused);
    }
    if (btn) {
      btn.addEventListener('click', function () {
        paused = !paused;
        velocity = 0;
        syncButton();
      });
    }

    /* ---- the drag ---- */
    track.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, from: offset, moved: false, t: e.timeStamp, lastX: e.clientX, v: 0 };
      velocity = 0;
    });

    track.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved) {
        /* A mostly vertical move is the page scrolling: let it go. */
        if (Math.abs(e.clientY - drag.y) > Math.abs(dx) && Math.abs(e.clientY - drag.y) > DRAG_START) { drag = null; return; }
        if (Math.abs(dx) < DRAG_START) return;
        drag.moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* not capturable */ }
      }
      var dt = Math.max(1, e.timeStamp - drag.t);
      var step = drag.lastX - e.clientX;
      drag.v = 0.8 * (step / dt) + 0.2 * drag.v;
      drag.t = e.timeStamp;
      drag.lastX = e.clientX;
      offset = wrap(drag.from - dx);
      paint();
      if (e.cancelable) e.preventDefault();
    });

    function endDrag(e) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      if (drag.moved) {
        swallowNext = true;
        setTimeout(function () { swallowNext = false; }, 400);
        velocity = Math.max(-3, Math.min(3, drag.v));
        track.classList.remove('is-dragging');
      }
      drag = null;
      last = 0;
      schedule();
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    /* Only the track's own capture ending counts. A finger is captured by
       the card it lands on, and moving that capture to the track makes the
       card fire lostpointercapture, which bubbles here mid drag. */
    track.addEventListener('lostpointercapture', function (e) {
      if (e.target === track) endDrag(e);
    });

    /* The click the browser fires after a drag would open the card the
       drag started on. */
    track.addEventListener('click', function (e) {
      if (!swallowNext) return;
      swallowNext = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* ---- hover and focus hold it still ---- */
    track.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hover = true; });
    track.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hover = false; });
    /* Keyboard focus only. A finger on a card focuses it too on some
       browsers, and that must neither jump the rail nor hold it still. */
    function keyboardFocus(n) {
      try { return n.matches(':focus-visible'); } catch (err) { return false; }
    }
    track.addEventListener('focusin', function (e) {
      /* Focus scrolls the clipped track by itself; undo that either way. */
      track.scrollLeft = 0;
      if (!keyboardFocus(e.target)) return;
      focused = true;
      var card = e.target.closest ? e.target.closest('.fxrv__card') : null;
      /* Move the rail instead, so the card is in view and the offset stays
         true. */
      if (card) {
        offset = wrap(card.offsetLeft - cards[0].offsetLeft);
        paint();
      }
    });
    track.addEventListener('focusout', function () { focused = false; });

    /* ---- only run when it can be seen ---- */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        last = 0;
        schedule();
      }).observe(section);
    }
    document.addEventListener('visibilitychange', function () { last = 0; schedule(); });

    var resizeTimer = 0;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { measure(); offset = wrap(offset); paint(); }, 120);
    });

    measure();
    syncButton();
    paint();
    schedule();
    /* Web fonts can land after the first measure and widen the cards. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measure(); offset = wrap(offset); });
  }

  function start() {
    var list = document.querySelectorAll('.fxrv');
    for (var i = 0; i < list.length; i++) init(list[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  /* The theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', start);
})();
